import { useState, useMemo } from 'react'
import { Search, Mail, MessageCircle, Briefcase, Phone, Plus, ChevronDown, X, CheckCircle, Clock, AlertCircle } from 'lucide-react'
import { useTicketsStore } from '../store/ticketsStore'
import { Modal, FormInput, FormSelect, FormTextarea, ActionControls, ConfirmDialog } from '../components/common/CRUDControls'
import AIHelpDeskAssistant from '../components/helpdesk/AIHelpDeskAssistant'

// Channel icons
const channelIcon = {
  email: <Mail className="w-4 h-4" />,
  discord: <MessageCircle className="w-4 h-4" />,
  linkedin: <Briefcase className="w-4 h-4" />,
  phone: <Phone className="w-4 h-4" />,
}

const priorityColors = {
  CRITICAL: 'bg-red-500/20 text-red-300 border border-red-500/50',
  HIGH: 'bg-orange-500/20 text-orange-300 border border-orange-500/50',
  MEDIUM: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50',
  LOW: 'bg-blue-500/20 text-blue-300 border border-blue-500/50',
}

const statusColors = {
  OPEN: 'bg-red-500/20 text-red-300',
  IN_PROGRESS: 'bg-blue-500/20 text-blue-300',
  RESOLVED: 'bg-green-500/20 text-green-300',
  ESCALATED: 'bg-orange-500/20 text-orange-300',
  PENDING: 'bg-yellow-500/20 text-yellow-300',
}

const statusIcons = {
  OPEN: <AlertCircle className="w-4 h-4" />,
  IN_PROGRESS: <Clock className="w-4 h-4" />,
  RESOLVED: <CheckCircle className="w-4 h-4" />,
  ESCALATED: <AlertCircle className="w-4 h-4" />,
  PENDING: <Clock className="w-4 h-4" />,
}

export default function TicketsPage() {
  const tickets = useTicketsStore((state) => state.getAllTickets())
  const addTicket = useTicketsStore((state) => state.addTicket)
  const updateTicket = useTicketsStore((state) => state.updateTicket)
  const deleteTicket = useTicketsStore((state) => state.deleteTicket)
  const addNote = useTicketsStore((state) => state.addNote)
  const stats = useTicketsStore((state) => state.getStatistics())

  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPriority, setFilterPriority] = useState('all')
  const [filterChannel, setFilterChannel] = useState('all')
  const [sortBy, setSortBy] = useState('created')
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [newNote, setNewNote] = useState('')

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    fromName: '',
    fromEmail: '',
    channel: 'email',
    priority: 'MEDIUM',
    status: 'OPEN',
    assignee: '',
  })

  // Filter and sort tickets
  const filteredTickets = useMemo(() => {
    let filtered = tickets.filter(ticket => {
      const matchesSearch =
        ticket.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.fromName.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus
      const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority
      const matchesChannel = filterChannel === 'all' || ticket.channel === filterChannel

      return matchesSearch && matchesStatus && matchesPriority && matchesChannel
    })

    // Sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
          return priorityOrder[a.priority] - priorityOrder[b.priority]
        case 'created':
        default:
          return new Date(b.created) - new Date(a.created)
      }
    })
  }, [tickets, searchQuery, filterStatus, filterPriority, filterChannel, sortBy])

  const handleAddTicket = () => {
    setFormData({
      subject: '',
      description: '',
      fromName: '',
      fromEmail: '',
      channel: 'email',
      priority: 'MEDIUM',
      status: 'OPEN',
      assignee: '',
    })
    setShowAddModal(true)
  }

  const handleSaveTicket = () => {
    if (formData.subject && formData.description) {
      addTicket(formData)
      setShowAddModal(false)
    }
  }

  const handleUpdateStatus = (ticketId, newStatus) => {
    updateTicket(ticketId, { status: newStatus })
  }

  const handleUpdatePriority = (ticketId, newPriority) => {
    updateTicket(ticketId, { priority: newPriority })
  }

  const handleAddNote = () => {
    if (newNote && selectedTicket) {
      addNote(selectedTicket.id, 'Support Agent', newNote)
      setNewNote('')
      // Refresh selected ticket
      const updated = useTicketsStore.getState().getTicketById(selectedTicket.id)
      setSelectedTicket(updated)
    }
  }

  const handleDeleteTicket = () => {
    if (selectedTicket) {
      deleteTicket(selectedTicket.id)
      setShowDetails(false)
      setShowDeleteConfirm(false)
      setSelectedTicket(null)
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleString()
  }

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000)
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    return `${Math.floor(seconds / 86400)}d ago`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Ticket Management System</h1>
          <p className="text-blue-200 mt-1">Professional helpdesk for all support channels</p>
        </div>
        <button
          onClick={handleAddTicket}
          className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded-lg flex items-center space-x-2 shadow-lg transition-all hover:shadow-blue-500/50"
        >
          <Plus className="w-5 h-5" />
          <span>Create Ticket</span>
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-4">
          <p className="text-sm text-blue-200">Total Tickets</p>
          <p className="text-3xl font-bold text-white mt-2">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-red-500/30 p-4">
          <p className="text-sm text-red-200">Critical/Open</p>
          <p className="text-3xl font-bold text-red-300 mt-2">{stats.critical + stats.open}</p>
        </div>
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-orange-500/30 p-4">
          <p className="text-sm text-orange-200">In Progress</p>
          <p className="text-3xl font-bold text-orange-300 mt-2">{stats.inProgress}</p>
        </div>
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-green-500/30 p-4">
          <p className="text-sm text-green-200">Resolved</p>
          <p className="text-3xl font-bold text-green-300 mt-2">{stats.resolved}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/20 p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-blue-400" />
          <input
            type="text"
            placeholder="Search by ticket ID, subject, or customer name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-400"
          >
            <option value="all">All Status</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="RESOLVED">Resolved</option>
            <option value="ESCALATED">Escalated</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-400"
          >
            <option value="all">All Priority</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>

          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-400"
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="discord">Discord</option>
            <option value="linkedin">LinkedIn</option>
            <option value="phone">Phone</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-400"
          >
            <option value="created">Newest First</option>
            <option value="priority">By Priority</option>
          </select>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-blue-500/20">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Ticket ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Subject</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Channel</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Priority</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Assignee</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Created</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-blue-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-500/10">
              {filteredTickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-bold text-blue-300">{ticket.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        setSelectedTicket(ticket)
                        setShowDetails(true)
                      }}
                      className="text-white hover:text-blue-300 transition-colors text-left max-w-xs truncate"
                    >
                      {ticket.subject}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">{ticket.fromName}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-1 text-gray-300">
                      {channelIcon[ticket.channel]}
                      <span className="text-sm capitalize">{ticket.channel}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${priorityColors[ticket.priority]}`}>
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-1">
                      {statusIcons[ticket.status]}
                      <span className={`px-2 py-1 rounded text-xs font-bold ${statusColors[ticket.status]}`}>
                        {ticket.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">{ticket.assignee || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{getTimeAgo(ticket.created)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        setSelectedTicket(ticket)
                        setShowDetails(true)
                      }}
                      className="text-blue-400 hover:text-blue-300 transition-colors text-sm font-semibold"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredTickets.length === 0 && (
          <div className="text-center py-8 text-gray-400">No tickets found matching your criteria</div>
        )}
      </div>

      {/* Create/Edit Ticket Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Create New Ticket"
        onSubmit={handleSaveTicket}
        submitLabel="Create Ticket"
      >
        <FormInput
          label="Subject"
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          placeholder="Brief description of the issue"
          required
        />
        <FormTextarea
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Detailed description of the ticket"
          rows={4}
          required
        />
        <FormInput
          label="Customer Name"
          value={formData.fromName}
          onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
          placeholder="Customer name"
        />
        <FormInput
          label="Customer Email"
          value={formData.fromEmail}
          onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
          placeholder="customer@example.com"
          type="email"
        />
        <FormSelect
          label="Channel"
          value={formData.channel}
          onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
          options={[
            { value: 'email', label: 'Email' },
            { value: 'discord', label: 'Discord' },
            { value: 'linkedin', label: 'LinkedIn' },
            { value: 'phone', label: 'Phone' },
          ]}
        />
        <FormSelect
          label="Priority"
          value={formData.priority}
          onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
          options={[
            { value: 'CRITICAL', label: 'Critical' },
            { value: 'HIGH', label: 'High' },
            { value: 'MEDIUM', label: 'Medium' },
            { value: 'LOW', label: 'Low' },
          ]}
        />
        <FormInput
          label="Assign To"
          value={formData.assignee}
          onChange={(e) => setFormData({ ...formData, assignee: e.target.value })}
          placeholder="Support team member name"
        />
      </Modal>

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <Modal
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          title={`Ticket ${selectedTicket.id} - Details`}
        >
          <div className="space-y-4">
            {/* Header Info */}
            <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
              <h3 className="text-xl font-bold text-white mb-3">{selectedTicket.subject}</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-blue-300 font-semibold">Status</p>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => {
                      handleUpdateStatus(selectedTicket.id, e.target.value)
                      const updated = useTicketsStore.getState().getTicketById(selectedTicket.id)
                      setSelectedTicket(updated)
                    }}
                    className="mt-1 px-2 py-1 bg-slate-700 border border-blue-500/30 rounded text-white"
                  >
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="ESCALATED">Escalated</option>
                  </select>
                </div>
                <div>
                  <p className="text-blue-300 font-semibold">Priority</p>
                  <select
                    value={selectedTicket.priority}
                    onChange={(e) => {
                      handleUpdatePriority(selectedTicket.id, e.target.value)
                      const updated = useTicketsStore.getState().getTicketById(selectedTicket.id)
                      setSelectedTicket(updated)
                    }}
                    className="mt-1 px-2 py-1 bg-slate-700 border border-blue-500/30 rounded text-white"
                  >
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
                <div>
                  <p className="text-blue-300 font-semibold">Assignee</p>
                  <p className="text-gray-300 mt-1">{selectedTicket.assignee || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-blue-300 font-semibold">Channel</p>
                  <p className="text-gray-300 mt-1 capitalize">{selectedTicket.channel}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
              <h4 className="text-sm font-semibold text-blue-300 mb-2">Description</h4>
              <p className="text-gray-300 text-sm leading-relaxed">{selectedTicket.description}</p>
            </div>

            {/* AI Help Desk Assistant */}
            <AIHelpDeskAssistant
              ticket={selectedTicket}
              onResponseGenerated={(response) => {
                // Auto-add AI-generated response as a note
                addNote(selectedTicket.id, 'AI Assistant', `[AI Suggested Response]\n\n${response}`)
                const updated = useTicketsStore.getState().getTicketById(selectedTicket.id)
                setSelectedTicket(updated)
              }}
              onAnalysisComplete={(analysis) => {
                console.log('AI Analysis:', analysis)
              }}
            />

            {/* Customer Info */}
            <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
              <h4 className="text-sm font-semibold text-blue-300 mb-2">Customer</h4>
              <p className="text-gray-300 text-sm">{selectedTicket.fromName}</p>
              <p className="text-gray-400 text-xs">{selectedTicket.fromEmail}</p>
            </div>

            {/* Notes */}
            <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
              <h4 className="text-sm font-semibold text-blue-300 mb-3">Notes & History</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                {selectedTicket.notes && selectedTicket.notes.map((note, idx) => (
                  <div key={idx} className="bg-slate-800 rounded p-2 border-l-2 border-blue-500">
                    <p className="text-xs text-blue-300 font-bold">{note.author} - {formatDate(note.date)}</p>
                    <p className="text-xs text-gray-300 mt-1">{note.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 px-2 py-1 text-sm bg-slate-700 border border-blue-500/30 rounded text-white placeholder-gray-400"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                />
                <button
                  onClick={handleAddNote}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded transition-colors"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
              <div>
                <p className="text-blue-300 font-semibold">Created</p>
                <p>{formatDate(selectedTicket.created)}</p>
              </div>
              <div>
                <p className="text-blue-300 font-semibold">Last Updated</p>
                <p>{formatDate(selectedTicket.updated)}</p>
              </div>
            </div>

            {/* Delete Button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full px-3 py-2 bg-red-600 hover:bg-red-500 text-white text-sm rounded transition-colors font-semibold"
            >
              Delete Ticket
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteTicket}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Delete Ticket"
        message={`Are you sure you want to delete ticket ${selectedTicket?.id}? This action cannot be undone.`}
      />
    </div>
  )
}
